import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoverImage } from '@/components/CoverImage';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Star, MapPin, Calendar } from 'lucide-react';

interface TimelineBook {
  id: string;
  title: string;
  authors?: string[];
  cover_url?: string;
  cover_cache_url?: string;
  isbn13?: string;
  isbn10?: string;
  finished_at: string;
  rating_star?: number;
}

interface ReadingTimelineProps {
  books: TimelineBook[];
  year: number;
}

// Group books by month
function groupBooksByMonth(books: TimelineBook[]): Record<string, TimelineBook[]> {
  const groups: Record<string, TimelineBook[]> = {};
  
  books.forEach(book => {
    const date = new Date(book.finished_at);
    const monthKey = format(date, 'MMMM');
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(book);
  });
  
  return groups;
}

export function ReadingTimeline({ books, year }: ReadingTimelineProps) {
  const navigate = useNavigate();
  
  const sortedBooks = useMemo(() => {
    return [...books].sort((a, b) => 
      new Date(a.finished_at).getTime() - new Date(b.finished_at).getTime()
    );
  }, [books]);
  
  const monthGroups = useMemo(() => groupBooksByMonth(sortedBooks), [sortedBooks]);
  const months = Object.keys(monthGroups);
  
  if (books.length === 0) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <Card className="card-elevated overflow-hidden">
        <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          Your Reading Journey
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {books.length} books completed in {year}
        </p>
      </CardHeader>
      <CardContent>
        {/* Horizontal scrollable timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-accent" />
          
          <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
            <div className="flex gap-6 min-w-max pt-2">
              {months.map((month, monthIndex) => (
                <div key={month} className="flex flex-col items-center min-w-[120px]">
                  {/* Month marker */}
                  <div className="relative z-10 flex flex-col items-center mb-4">
                    <div className="w-4 h-4 rounded-full bg-primary border-2 border-background shadow-md" />
                    <span className="mt-2 text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {month}
                    </span>
                  </div>
                  
                  {/* Books in this month */}
                  <div className="flex flex-col gap-3 pt-8">
                    {monthGroups[month].map((book, bookIndex) => (
                      <Tooltip key={book.id}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => navigate(`/book/${book.id}`)}
                            className="group relative flex flex-col items-center transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
                          >
                            <div className="relative">
                              <CoverImage
                                bookId={book.id}
                                isbn13={book.isbn13}
                                isbn10={book.isbn10}
                                title={book.title}
                                author={book.authors?.[0]}
                                size="sm"
                                className="w-16 h-24 rounded-md shadow-lg group-hover:shadow-xl transition-shadow"
                              />
                              {book.rating_star && (
                                <div className="absolute -bottom-2 -right-2 flex items-center gap-0.5 bg-background border rounded-full px-1.5 py-0.5 shadow-sm">
                                  <Star className="w-3 h-3 fill-star text-star" />
                                  <span className="text-xs font-medium">{book.rating_star}</span>
                                </div>
                              )}
                            </div>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[200px] text-center">
                          <p className="font-medium">{book.title}</p>
                          {book.authors?.[0] && (
                            <p className="text-xs text-muted-foreground">{book.authors[0]}</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-8 pt-4 border-t text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>Tap a book to view details</span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 fill-star text-star" />
            <span>Your rating</span>
          </div>
        </div>
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
